import {getId, getType} from 'apis/model/base'
import React, {useCallback, useContext, useMemo} from 'react'
import {
    PiCertificateLight,
    PiChalkboardSimpleBold,
    PiChalkboardTeacherFill,
    PiChartLineBold,
    PiGraduationCapFill,
    PiMonitorLight,
    PiUserCirclePlusFill,
    PiUserSwitch,
} from 'react-icons/pi'
import {RiSubscript} from 'react-icons/ri'
import {compose, fromRenderProps, nest, withProps} from 'recompose'
import {DelayRender} from 'views/Discovery/DelayRender'
import {SettingsMenu} from 'views/AppLocals/factory/createSettingLayout'
import DefaultTemplate from 'views/AppLocals/Templates/DefaultTemplate'
import {renderOwnChild} from 'views/Shared'
import CustomSiderMenu from 'views/Shared/components/CustomSiderMenu'
import {Queries} from 'views/Shared/enums/Queries'
import {useMenuHandler} from 'views/Shared/handlers/useMenuHandler'
import WorkspaceContext from '../WorkspaceContext'
import CourseClasses from './containers/CourseClasses'
import CourseClassTimetable from './containers/CourseClassTimetable'
import CourseEnrollments from './containers/CourseEnrollments'
import Students from './containers/Students'
import Subjects from './containers/Subjects'
import Teachers from './containers/Teachers'
import CourseClassAttendanceReport from './reports/CourseClassAttendanceReport'
import CourseClassStudentGradeSheetReport from './reports/CourseClassStudentGradeSheetReport'
import CourseClassStudentAttendanceSheetReport from './reports/CourseClassStudentAttendanceSheetReport'
import TransferReport from './reports/TransferReport'
import useTranslate from "../../../modules/local/useTranslate";
import {BiSolidReport} from "react-icons/bi";
import {HiOutlineDocumentReport} from "react-icons/hi";
import CourseClassAttendanceStatsReport from "./reports/CourseClassAttendanceStatsReport";
import CourseClassStudentResultReport from "./reports/CourseClassStudentResultReport";
import StudentResultReport from "./reports/StudentResultReport";
import CourseStudentResultReport from "./reports/CourseStudentResultReport";
import CourseMedals from "./containers/CourseMedals";
import CourseDashboard from "./containers/CourseDashboard";
import CourseContainer from "./containers/CourseContainer";

const ItemKeys = Object.freeze({
    DASHBOARD: 'dashboard',
    COUNTER_PARTY: 'counter-party',
    TEACHERS: 'teachers',
    TEACHER: 'teacher',
    SUPERVISOR: 'supervisor',
    ASSISTANT: 'assistant',
    STUDENT: 'student',
    ENROLLMENT: 'enrollment',
    REGISTER_FOR_COURSE: 'register-for-course',
    COURSE_SET: 'course-set',
    COURSES: 'courses',
    COURSE: 'course',
    COURSE_CLASS_SET: 'course-class-set',
    COURSE_CLASS: 'course-class',
    COURSE_CLASS_TIMETABLE: 'course-class-timetable',
    SUBJECT: 'subject',
    COURSE_SUBJECT: 'course-subject',
    COURSE_CLASS_TEACHER: 'course-class-teacher',
    COURSE_CLASS_STUDENT: 'course-class-student',
    REPORT: 'report',
    ATTENDANCE_REPORT: 'attendance-report',
    SCORE_REPORT: 'score-report',
    COURSE_CLASS_ATTENDANCE_REPORT: 'course-class-attendance-report',
    COURSE_CLASS_STUDENT_ATTENDANCE_SHEET_REPORT: 'course-class-student-attendance-sheet-report',
    COURSE_CLASS_ATTENDANCE_STATS_REPORT: 'course-class-attendance-stats-report',
    COURSE_CLASS_STUDENT_GRADE_SHEET_REPORT: 'course-class-grade-report',
    COURSE_CLASS_STUDENT_RESULTS_REPORT: 'course-class-student-results-report',
    STUDENT_RESULTS_REPORT: 'student-results-report',
    COURSE_STUDENT_RESULTS_REPORT: 'course-student-results-report',
    STUDENT_TRANSFER_REPORT: 'student-transfer-report',
    COURSE_SET_CHILDREN: 'COURSE_SET_CHILDREN',
    COURSE_IN_LOOKUP: 'course-in-lookup',
    COURSE_MEDALS: 'course-medals',
})

export const CourseStates = [
    {
        value: 'enrollment_open',
        label: 'enrollment open',
        className: 'bg-green-50 text-green-600',
    },
    {
        value: 'in_process',
        label: 'in process',
        className: 'bg-blue-100 text-blue-500',
    },
    {
        value: 'completed',
        label: 'completed',
        className: 'bg-primary-50 text-primary-600',
    },
]

const WorkspaceCourse = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext);
    const menuItems = useMemo(() => {
        const menuItems = [
            {
                key: ItemKeys.DASHBOARD,
                label: 'overview',
                icon: <PiMonitorLight size={16}/>,
            },
            {
                key: ItemKeys.COURSE_MEDALS,
                label: 'course certificates',
                icon: <PiCertificateLight size={16}/>,
            },
            {
                key: ItemKeys.SUBJECT,
                label: 'subjects',
                icon: <RiSubscript size={16}/>,
            },
            {
                key: ItemKeys.COURSE,
                label: 'course management',
                icon: <PiChalkboardSimpleBold size={16}/>,
            },
            {
                key: ItemKeys.TEACHER,
                label: 'teacher/supervisor',
                icon: <PiChalkboardTeacherFill size={16}/>,
            },
            {
                key: ItemKeys.STUDENT,
                label: 'students',
                icon: <PiGraduationCapFill size={16}/>,
            },
            {
                key: ItemKeys.REGISTER_FOR_COURSE,
                label: 'enrollments',
                icon: <PiUserCirclePlusFill size={16}/>,
            },
            {
                key: ItemKeys.REPORT,
                label: 'report',
                icon: <PiChartLineBold size={16}/>,
                children: [
                    {
                        key: ItemKeys.STUDENT_TRANSFER_REPORT,
                        label: 'transfer records',
                        icon: <PiUserSwitch size={16}/>,
                    },
                    {
                        key: ItemKeys.ATTENDANCE_REPORT,
                        label: 'attendances',
                        icon: <HiOutlineDocumentReport size={16}/>,
                        children: [
                            {
                                key: ItemKeys.COURSE_CLASS_ATTENDANCE_REPORT,
                                label: 'attendance by class',
                                icon: <BiSolidReport size={14}/>,
                            },
                            {
                                key: ItemKeys.COURSE_CLASS_ATTENDANCE_STATS_REPORT,
                                label: 'attendance ratio report',
                                icon: <BiSolidReport size={16}/>,
                            },
                            {
                                key: ItemKeys.COURSE_CLASS_STUDENT_ATTENDANCE_SHEET_REPORT,
                                label: 'attendance sheet report',
                                icon: <BiSolidReport size={16}/>,
                            },
                        ],
                    },
                    {
                        key: ItemKeys.SCORE_REPORT,
                        label: 'grades',
                        icon: <HiOutlineDocumentReport size={16}/>,
                        children: [
                            {
                                key: ItemKeys.COURSE_CLASS_STUDENT_RESULTS_REPORT,
                                label: 'grade by class',
                                icon: <BiSolidReport size={16}/>,
                            },
                            {
                                key: ItemKeys.STUDENT_RESULTS_REPORT,
                                label: 'detail report of student',
                                icon: <BiSolidReport size={16}/>,
                            },
                            {
                                key: ItemKeys.COURSE_STUDENT_RESULTS_REPORT,
                                label: 'student results by class',
                                icon: <BiSolidReport size={16}/>,
                            },
                            {
                                key: ItemKeys.COURSE_CLASS_STUDENT_GRADE_SHEET_REPORT,
                                label: 'grade sheet report',
                                icon: <BiSolidReport size={16}/>,
                            },
                        ],
                    },
                ],
            },
        ]
        return menuItems;
    }, [])
    const routeEntities = useMemo(() => {
        const routeEntities = [
            {
                key: ItemKeys.DASHBOARD,
                component: CourseDashboard,
            },
            {
                key: ItemKeys.COURSE,
                component: CourseContainer,
            },
            {
                key: ItemKeys.TEACHER,
                component: Teachers,
            },
            {
                key: ItemKeys.STUDENT,
                component: Students,
            },
            {
                key: ItemKeys.SUBJECT,
                component: Subjects,
            },
            {
                key: ItemKeys.COURSE_CLASS,
                component: CourseClasses,
            },
            {
                key: ItemKeys.COURSE_CLASS_TIMETABLE,
                component: CourseClassTimetable,
            },
            {
                key: ItemKeys.REGISTER_FOR_COURSE,
                component: CourseEnrollments,
            },
            {
                key: ItemKeys.STUDENT_TRANSFER_REPORT,
                component: TransferReport,
            },
            {
                key: ItemKeys.COURSE_MEDALS,
                component: CourseMedals,
            },
            {
                key: ItemKeys.COURSE_CLASS_ATTENDANCE_REPORT,
                component: CourseClassAttendanceReport,
            },
            {
                key: ItemKeys.COURSE_CLASS_ATTENDANCE_STATS_REPORT,
                component: CourseClassAttendanceStatsReport,
            },
            {
                key: ItemKeys.COURSE_CLASS_STUDENT_ATTENDANCE_SHEET_REPORT,
                component: CourseClassStudentAttendanceSheetReport,
            },

            {
                key: ItemKeys.COURSE_CLASS_STUDENT_RESULTS_REPORT,
                component: CourseClassStudentResultReport,
            },
            {
                key: ItemKeys.STUDENT_RESULTS_REPORT,
                component: StudentResultReport,
            },
            {
                key: ItemKeys.COURSE_STUDENT_RESULTS_REPORT,
                component: CourseStudentResultReport,
            },
            {
                key: ItemKeys.COURSE_CLASS_STUDENT_GRADE_SHEET_REPORT,
                component: CourseClassStudentGradeSheetReport,
            },
        ].map(({key, ...rest}) => ({
            key,
            path: key,
            ...rest,
        }))
        return routeEntities;
    }, [])

    const {activeKey, Component, onMenuSelect} = useMenuHandler({
        routes: routeEntities,
        actionName: Queries.REL,
    })
    const renderMenu = useCallback(
        () => (
            <CustomSiderMenu
                deps={[activeKey]}
                Wrapper={renderOwnChild}>
                <SettingsMenu
                    items={menuItems}
                    onSelect={onMenuSelect}
                    selectedKeys={[activeKey]}
                    // defaultSelectedKeys={[activeKey]}
                    defaultOpenKeys={[]}
                />
            </CustomSiderMenu>
        ),
        [menuItems, activeKey]
    )

    return (
        <DefaultTemplate renderMenu={renderMenu}>
            <div className="space-y-3 p-2">
                <Component {...{owner_id, owner_type}} />
            </div>
        </DefaultTemplate>
    )
}

export default nest(
    withProps({lazy: true})(DelayRender),
    compose(
        fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
            owner_id: getId(organization),
            owner_type: getType(organization),
        }))
    )(WorkspaceCourse)
)
